import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useApp, API } from "../context";

// Persistent screen stickers (a sun in the corner, a donkey 🫏, etc.) that STAY
// on every page until removed. Draggable + resizable in your Universe; shows
// only promoted ones in Normal. Any emoji works, not just a fixed list.
export const STICKER_EMOJI = {
  sun: "☀️", moon: "🌙", star: "⭐", crown: "👑", rainbow: "🌈", heart: "❤️",
  fire: "🔥", cloud: "☁️", lightning: "⚡", flower: "🌸", diamond: "💎",
  rocket: "🚀", planet: "🪐", trophy: "🏆",
};
const SIZE_PX = { small: 30, medium: 46, large: 66, huge: 96 };
const SIZE_ORDER = ["small", "medium", "large", "huge"];
const CORNER_STYLE = {
  "top-left": { top: 14, left: 14 },
  "top-right": { top: 14, right: 14 },
  "bottom-left": { bottom: 96, left: 14 },
  "bottom-right": { bottom: 96, right: 88 },
  center: { top: "45%", left: "50%", transform: "translate(-50%, -50%)" },
};

export default function FounderStickers({ enabled }) {
  const { token } = useApp();
  const [stickers, setStickers] = useState([]);
  const [reality, setReality] = useState("normal");
  const drag = useRef(null);

  const load = () => {
    axios.get(`${API}/founder/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const s = r.data?.settings || {};
        const rl = s.reality || "normal";
        setReality(rl);
        setStickers(rl === "command" ? (s.stickers || []) : (s.promoted_stickers || []));
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!enabled || !token) return undefined;
    load();
    window.addEventListener("founder:cosmetics-updated", load);
    return () => window.removeEventListener("founder:cosmetics-updated", load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, token]);

  const canEdit = reality === "command"; // only rearrange in your Universe

  const onPointerDown = (e, st) => {
    if (!canEdit) return;
    e.preventDefault();
    drag.current = { id: st.id, moved: false };
    try { e.target.setPointerCapture(e.pointerId); } catch (_) {}
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    drag.current.moved = true;
    const x = Math.max(0, Math.min(100, (e.clientX / window.innerWidth) * 100));
    const y = Math.max(0, Math.min(100, (e.clientY / window.innerHeight) * 100));
    setStickers((prev) => prev.map((s) => (s.id === drag.current.id ? { ...s, x, y } : s)));
  };
  const onPointerUp = (st) => {
    if (!drag.current) return;
    const moved = drag.current.moved;
    drag.current = null;
    const cur = stickers.find((s) => s.id === st.id);
    if (moved && cur) {
      axios.post(`${API}/founder/sticker/move`, { id: st.id, x: cur.x, y: cur.y },
        { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
  };
  const cycleSize = (st) => {
    if (!canEdit) return;
    const next = SIZE_ORDER[(SIZE_ORDER.indexOf(st.size || "medium") + 1) % SIZE_ORDER.length];
    setStickers((prev) => prev.map((s) => (s.id === st.id ? { ...s, size: next } : s)));
    axios.post(`${API}/founder/sticker/move`, { id: st.id, size: next },
      { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  if (!stickers.length) return null;

  return createPortal(
    <>
      {stickers.map((st) => {
        const free = st.x != null && st.y != null;
        const pos = free
          ? { left: `${st.x}%`, top: `${st.y}%`, transform: "translate(-50%, -50%)" }
          : (CORNER_STYLE[st.corner] || CORNER_STYLE["top-left"]);
        const px = SIZE_PX[st.size] || SIZE_PX.medium;
        const emoji = STICKER_EMOJI[st.icon] || st.icon || "⭐";
        const motion = st.motion || "pulse";
        const motionClass = motion === "spin" ? "cc-fx-spin" : motion === "pulse" ? "cc-fx-pulse" : "";
        return (
          <div key={st.id}
            className={`fixed z-[2147481000] select-none ${canEdit ? "cursor-grab" : "pointer-events-none"}`}
            style={{ ...pos, fontSize: px, filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.35))", touchAction: "none" }}
            onPointerDown={(e) => onPointerDown(e, st)}
            onPointerMove={onPointerMove}
            onPointerUp={() => onPointerUp(st)}
            onDoubleClick={() => cycleSize(st)}
            title={canEdit ? "Drag to move · double-click to resize" : undefined}
            data-testid={`founder-sticker-${STICKER_EMOJI[st.icon] ? st.icon : "emoji"}`}>
            <span className={motionClass} style={{ display: "inline-block" }}>{emoji}</span>
          </div>
        );
      })}
    </>,
    document.body,
  );
}
